import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { capture } from "@/lib/sentry/capture";
import { toast } from "sonner";
import { logger } from "@/utils/logger";

import type { PMIssueChecklistItem } from "@/domains/pm/types";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import {
  createChecklistItems,
  deleteChecklistItem,
  isStaleChecklistOrder,
  listChecklistItems,
  reorderChecklistItems,
  subscribeToIssueChecklist,
  updateChecklistItem,
} from "@/stores/pm/repository";

const EMPTY: PMIssueChecklistItem[] = [];

export interface IssueChecklist {
  items: PMIssueChecklistItem[];
  /** False while the table is not deployed — the section hides itself. */
  available: boolean;
  isLoading: boolean;
  loadFailed: boolean;
  /** Append items in the order given. */
  add: (texts: string[]) => Promise<void>;
  setDone: (id: number, done: boolean) => Promise<void>;
  rename: (id: number, text: string) => Promise<void>;
  remove: (id: number) => Promise<void>;
  /** Store the whole list in this order. */
  reorder: (orderedIds: number[]) => Promise<void>;
}

/** Items added optimistically carry a negative id until the insert returns. */
export function isPendingChecklistItem(item: PMIssueChecklistItem): boolean {
  return item.id < 0;
}

function counts(items: readonly PMIssueChecklistItem[]) {
  return {
    checklist_total: items.length,
    checklist_done: items.filter((i) => i.is_done).length,
  };
}

function checklistItems(items: PMIssueChecklistItem[] | null | undefined): PMIssueChecklistItem[] {
  return items ?? EMPTY;
}

function applyChecklistSettlement(
  client: ReturnType<typeof useQueryClient>,
  key: ChecklistKey,
  settle: ChecklistChange | void
): void {
  if (settle) client.setQueryData<PMIssueChecklistItem[]>(key, (rows) => settle(rows ?? EMPTY));
}

type ChecklistKey = readonly ["pm-issue-checklist", number];
type ChecklistChange = (items: PMIssueChecklistItem[]) => PMIssueChecklistItem[];
type ChecklistWrite = () => Promise<ChecklistChange | void>;

function reportChecklistWrite(
  error: unknown,
  issueId: number,
  operation: string,
  t: ReturnType<typeof useLanguage>["t"]
): void {
  if (isStaleChecklistOrder(error)) {
    toast.info(t.pm.issues.checklist.stale);
    return;
  }
  toast.error(friendlyIssueError(error, t));
  const failure = error instanceof Error ? error : new Error(String(error));
  const eventId = capture(failure, {
    tags: { area: "pm", operation: `issue_checklist_${operation}` },
    context: { checklist: { issueId } },
    fingerprint: ["pm", "issue-checklist", operation],
  });
  void logger.error("database", "issue_checklist_write", "Issue checklist write refused", failure, {
    issueId,
    operation,
    sentry_event_id: eventId,
  });
}

function useChecklistWrites(issueId: number, active: boolean, queryKey: ChecklistKey) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const issuesStore = useIssuesStore();
  /**
   * Writes still on the wire, and whether a change arrived meanwhile. A re-read
   * started during a write can return the list from before it and paint over
   * the optimistic one, so echoes wait for the writes to settle and then
   * re-read once.
   */
  const inFlight = useRef(0);
  const staleWhileSaving = useRef(false);
  const refresh = useCallback(() => {
    if (inFlight.current > 0) {
      staleWhileSaving.current = true;
      return;
    }
    void queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  useEffect(() => {
    if (!active) return;
    const handle = subscribeToIssueChecklist(issueId, refresh);
    return () => handle.unsubscribe();
  }, [active, issueId, refresh]);

  const read = useCallback(
    () => queryClient.getQueryData<PMIssueChecklistItem[] | null>(queryKey) ?? EMPTY,
    [queryClient, queryKey]
  );

  /**
   * Apply `change` to the cache, run `write`, and undo on refusal. `write`
   * may return a replacement for the cached list (an insert hands back the
   * real ids).
   */
  const mutate = useCallback(
    async (operation: string, change: ChecklistChange, write: ChecklistWrite) => {
      // Counted BEFORE the first await: an echo arriving during it would
      // otherwise see nothing in flight and re-read over the optimistic list.
      inFlight.current += 1;
      let previous: PMIssueChecklistItem[] | null | undefined;
      let held: ReturnType<typeof counts> | null = null;
      try {
        await queryClient.cancelQueries({ queryKey });
        previous = queryClient.getQueryData<PMIssueChecklistItem[] | null>(queryKey);
        const next = change(checklistItems(previous));
        queryClient.setQueryData(queryKey, next);
        held = counts(next);
        issuesStore.updateIssueOptimistic(issueId, held);
        issuesStore.holdEdit(issueId, held);

        const settle = await write();
        applyChecklistSettlement(queryClient, queryKey, settle);
      } catch (e) {
        queryClient.setQueryData(queryKey, previous);
        issuesStore.updateIssueOptimistic(issueId, counts(checklistItems(previous)));
        reportChecklistWrite(e, issueId, operation, t);
        // The rollback may also have undone another write still in flight, so
        // settle on what the server holds once they are all done.
        staleWhileSaving.current = true;
      } finally {
        if (held) issuesStore.releaseEdit(issueId, held);
        inFlight.current -= 1;
        if (inFlight.current === 0 && staleWhileSaving.current) {
          staleWhileSaving.current = false;
          refresh();
        }
      }
    },
    [queryClient, queryKey, issuesStore, issueId, t, refresh]
  );

  return { read, mutate };
}

function useChecklistReadFailure(error: unknown, issueId: number): void {
  useEffect(() => {
    if (!error) return;
    const failure = error instanceof Error ? error : new Error(String(error));
    const eventId = capture(failure, {
      tags: { area: "pm", operation: "issue_checklist_read" },
      context: { checklist: { issueId } },
      fingerprint: ["pm", "issue-checklist", "read"],
    });
    void logger.error("database", "issue_checklist_read", "Issue checklist read failed", failure, {
      issueId,
      sentry_event_id: eventId,
    });
  }, [error, issueId]);
}

/**
 * One issue's checklist, kept live.
 *
 * Owns its realtime subscription rather than leaning on
 * `usePMIssueLiveUpdates`: the detail panel also opens inside the IFC viewer,
 * drawing compare and the protocol export, where no issues page is mounted.
 *
 * Every write is applied to the cache first and undone with a toast if the
 * server refuses it. The progress counts are pushed into the issues store as
 * they change — and HELD there while a write is on the wire — so the list row
 * and board card badges move with the tick instead of a reload later, and a
 * reload that raced the write cannot put the old count back.
 */
export function useIssueChecklist(issueId: number, enabled = true): IssueChecklist {
  const active = enabled && Number.isFinite(issueId);
  const queryKey = useMemo(() => ["pm-issue-checklist", issueId] as const, [issueId]);

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => listChecklistItems(issueId),
    enabled: active,
    staleTime: 30_000,
  });

  useChecklistReadFailure(error, issueId);

  const { read, mutate } = useChecklistWrites(issueId, active, queryKey);

  const actions = useChecklistActions(issueId, read, mutate);

  return {
    items: data ?? EMPTY,
    available: data !== null,
    isLoading: active && isLoading,
    loadFailed: Boolean(error),
    ...actions,
  };
}

function useChecklistActions(
  issueId: number,
  read: ReturnType<typeof useChecklistWrites>["read"],
  mutate: ReturnType<typeof useChecklistWrites>["mutate"]
) {
  const tempId = useRef(0);

  const add = useCallback(
    async (texts: string[]) => {
      if (texts.length === 0) return;
      const current = read();
      const start = current.reduce((max, i) => Math.max(max, i.position + 1), 0);
      const drafts: PMIssueChecklistItem[] = texts.map((text, i) => ({
        id: (tempId.current -= 1),
        issue_id: issueId,
        text,
        position: start + i,
        is_done: false,
        done_at: null,
        done_by: null,
      }));
      const draftIds = new Set(drafts.map((d) => d.id));
      await mutate(
        "add",
        (items) => [...items, ...drafts],
        async () => {
          const created = await createChecklistItems(issueId, texts, start);
          const byPosition = new Map(created.map((row) => [row.position, row]));
          return (items) =>
            items.map((item) =>
              draftIds.has(item.id) ? (byPosition.get(item.position) ?? item) : item
            );
        }
      );
    },
    [issueId, mutate, read]
  );

  const setDone = useCallback(
    (id: number, done: boolean) =>
      mutate(
        "toggle",
        (items) => items.map((i) => (i.id === id ? { ...i, is_done: done } : i)),
        async () => {
          const row = await updateChecklistItem(id, { is_done: done });
          // Carry the server's attribution back into the cache.
          return (items) => items.map((i) => (i.id === id ? { ...i, ...row } : i));
        }
      ),
    [mutate]
  );

  const rename = useCallback(
    (id: number, text: string) =>
      mutate(
        "rename",
        (items) => items.map((i) => (i.id === id ? { ...i, text } : i)),
        async () => {
          const row = await updateChecklistItem(id, { text });
          return (items) => items.map((i) => (i.id === id ? { ...i, ...row } : i));
        }
      ),
    [mutate]
  );

  const remove = useCallback(
    (id: number) =>
      mutate(
        "delete",
        (items) => items.filter((i) => i.id !== id),
        () => deleteChecklistItem(id)
      ),
    [mutate]
  );

  const reorder = useCallback(
    async (orderedIds: number[]) => {
      // An item still being inserted has no id the server knows; the section
      // disables dragging until the insert lands.
      if (orderedIds.some((id) => id < 0)) return;
      const rank = new Map(orderedIds.map((id, i) => [id, i]));
      await mutate(
        "reorder",
        (items) =>
          items
            .map((i) => ({ ...i, position: rank.get(i.id) ?? i.position }))
            .sort((a, b) => a.position - b.position || a.id - b.id),
        () => reorderChecklistItems(issueId, orderedIds)
      );
    },
    [issueId, mutate]
  );

  return { add, setDone, rename, remove, reorder };
}
